import { Link } from 'react-router-dom';
import { DocSection } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'trim', label: 'Trim before you export' },
  { id: 'select', label: 'Select results' },
  { id: 'export-timeline', label: 'Export to a timeline' },
  { id: 'place-panel', label: 'Place clips live in the Premiere panel' },
  { id: 'source-monitor', label: 'Source Monitor handoff' },
  { id: 'transcript-segments', label: 'Add transcript segments / captions' },
];

export default function Body() {
  return (
    <>
      <DocSection id="trim" title="Trim before you export">
        <p>
          A rough cut starts with results trimmed to just the part you want. Hover a result card
          and click <strong>Adjust</strong> to open a trim modal and set new in and out points for
          that clip. Adjust is a desktop-app action — the panel doesn&rsquo;t have its own trim
          step, since you can refine a placed clip directly on the Premiere timeline instead.
        </p>
        <DocShot slug="build-a-rough-cut" shot="adjust" alt="The Adjust modal open on a result card, with in/out trim handles" />
      </DocSection>

      <DocSection id="select" title="Select results">
        <p>
          Select the clips you want to send to a timeline — the results toolbar shows how many
          you&rsquo;ve picked. <strong>Deselect</strong> drops the current selection;{' '}
          <strong>Clear All</strong> resets the whole results list (with a confirmation, since it
          also clears the search blocks themselves).
        </p>
      </DocSection>

      <DocSection id="export-timeline" title="Export to a timeline">
        <p>
          On the desktop app, click <strong>Export N to timeline</strong> in the results toolbar.
          ClipRadar writes a timeline XML file with your trims already applied, saved to
          your <Link to="/docs/interface/settings">Export folder</Link>. Import that file into
          Premiere Pro, DaVinci Resolve, or Final Cut to bring the selected clips in as a sequence
          — there&rsquo;s no live connection to Resolve or Final Cut, so treat the import as
          one-way. After export, a card offers <strong>Show in folder</strong> and{' '}
          <strong>Re-export</strong>.
        </p>
        <DocShot slug="build-a-rough-cut" shot="export" alt="The results toolbar with clips selected and Export N to timeline highlighted" />
      </DocSection>

      <DocSection id="place-panel" title="Place clips live in the Premiere panel">
        <p>
          Inside the <Link to="/docs/platforms/premiere-panel">Premiere panel</Link>, selected
          clips go straight onto your open sequence instead of exporting a file — click{' '}
          <strong>Place N</strong> in the toolbar, or use a card&rsquo;s own{' '}
          <strong>Add to timeline</strong> buttons. Set the placement mode once and it applies to
          everything you place:
        </p>
        <ul>
          <li><strong>Smart</strong> — adds to a free track without disturbing anything already there (the default).</li>
          <li><strong>Insert</strong> — ripples everything after the playhead to make room.</li>
          <li><strong>Append</strong> — adds to the end of the sequence.</li>
        </ul>
        <DocShot slug="build-a-rough-cut" shot="place-buttons" alt="A panel result card's Add to timeline buttons and the Smart / Insert / Append toggle" panel />
        <DocShot slug="build-a-rough-cut" shot="placed-clip" alt="A clip freshly placed on the open Premiere sequence, panel visible alongside the timeline" panel />
      </DocSection>

      <DocSection id="source-monitor" title="Source Monitor handoff">
        <p>
          Not ready to commit a clip to the sequence yet? In the panel, a video or speech
          card&rsquo;s <strong>Open in Source Monitor</strong> button sends it to Premiere&rsquo;s
          Source Monitor with its in and out points already set to the matched segment, so you can
          scrub and confirm before placing it.
        </p>
      </DocSection>

      <DocSection id="transcript-segments" title="Add transcript segments / captions">
        <p>
          For speech results, open <strong>Show transcript</strong> (desktop) or{' '}
          <strong>Open transcript</strong> (panel) to bring up the full transcript scrolled to the
          matched line. Multi-select the segments you want, then either{' '}
          <strong>Add to timeline</strong> when the panel is connected, or{' '}
          <strong>Export SRT</strong> to save them as a caption file. See{' '}
          <Link to="/docs/concepts/speech-search">Speech search</Link> for more on the transcript
          viewer.
        </p>
      </DocSection>
    </>
  );
}
